<script setup>
import { computed, reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  endpointUrl: {
    type: String,
    default: '',
  },
  requestTemplate: {
    type: String,
    default: '',
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  tried: {
    type: Boolean,
    default: false,
  },
  succeeded: {
    type: Boolean,
    default: false,
  },
  result: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['try']);

const { t } = useI18n();
const values = reactive({});

const fields = computed(() => {
  const source = `${props.endpointUrl}\n${props.requestTemplate}`;
  const names = [];
  const pattern = /\{\{\s*([^{}]+?)\s*\}\}/g;
  let match = pattern.exec(source);
  while (match) {
    const name = match[1].trim();
    if (name && !names.includes(name)) names.push(name);
    match = pattern.exec(source);
  }
  return names;
});

watch(
  fields,
  names => {
    names.forEach(name => {
      if (values[name] === undefined) values[name] = '';
    });
  },
  { immediate: true }
);

const labelFor = name => name.replace(/[_-]+/g, ' ');

const view = computed(() => {
  if (!props.tried) return null;
  const raw = String(props.result || '');
  const technical =
    /^(Configuration error|Tool request failed|Unsupported)/.test(raw) ||
    raw.includes('is not allowed');
  const failed = !props.succeeded || technical;
  const text = raw
    .replace(/^HTTP \d{3}( error)?\n?/, '')
    .replace(/\n*Instrucciones para interpretar la respuesta:\n[\s\S]*$/, '')
    .trim();
  return {
    failed,
    text: failed || technical ? '' : text,
  };
});

const run = () => {
  const params = {};
  fields.value.forEach(name => {
    params[name] = values[name];
  });
  emit('try', params);
};
</script>

<template>
  <section class="flex flex-col gap-3 pt-2 border-t border-n-weak">
    <h2 class="text-base font-medium text-n-slate-12">
      {{ t('ONIX.ACTIONS.TRY_TITLE') }}
    </h2>
    <p class="m-0 text-sm text-n-slate-11">
      {{ t('ONIX.ACTIONS.TRY_HINT') }}
    </p>
    <Input
      v-for="name in fields"
      :key="name"
      v-model="values[name]"
      :label="labelFor(name)"
    />
    <Button
      :label="t('ONIX.ACTIONS.TRY')"
      size="sm"
      color="slate"
      class="self-start"
      :is-loading="isLoading"
      @click="run"
    />
    <p v-if="view && !view.failed" class="m-0 text-sm text-n-teal-11">
      {{ t('ONIX.ACTIONS.TRY_OK') }}
    </p>
    <p v-else-if="view && view.failed" class="m-0 text-sm text-n-ruby-11">
      {{ t('ONIX.ACTIONS.TRY_FAIL') }}
    </p>
    <p
      v-if="view && view.text"
      class="m-0 text-sm text-n-slate-12 whitespace-pre-wrap rounded-xl bg-n-alpha-1 p-3"
    >
      {{ view.text }}
    </p>
  </section>
</template>
